import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ChevronUp, ChevronDown } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const toKey = (s) => s.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "");

export default function FieldsDialog({ open, onOpenChange, fields, onSaved }) {
  const [list, setList] = useState([]);
  const [label, setLabel] = useState("");
  const [type, setType] = useState("text");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setList(fields); }, [open, fields]);

  const add = () => {
    const key = toKey(label);
    if (!key) return;
    if (list.some((f) => f.key === key)) return toast.error(`Field ${key} already exists`);
    setList([...list, { key, label: label.trim(), type }]);
    setLabel("");
  };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const arr = [...list];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setList(arr);
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/card-fields", { fields: list });
      onSaved(data);
      toast.success("Fields saved");
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-slate-900 border-slate-800" data-testid="fields-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Card entry fields</DialogTitle>
          <DialogDescription>These fields appear on the entry form for every layout. The key (e.g. NAME) matches {"{{NAME}}"} placeholders in designs.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 max-h-[45vh] overflow-y-auto pr-1">
          {list.map((f, i) => (
            <li key={f.key} className="flex items-center gap-2" data-testid={`field-row-${f.key}`}>
              <input className="cf-input h-8 text-xs flex-1 min-w-0" value={f.label} data-testid={`field-label-${f.key}`}
                onChange={(e) => setList(list.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <span className="font-mono text-[10px] text-indigo-300 w-28 shrink-0 truncate">{`{{${f.key}}}`}</span>
              <select className="cf-input h-8 text-xs shrink-0" style={{ width: 80 }} value={f.type} disabled={f.key === "CERT_NO"} data-testid={`field-type-${f.key}`}
                onChange={(e) => setList(list.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)))}>
                <option value="text">Text</option><option value="image">Photo</option>
              </select>
              <button onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-white" data-testid={`field-up-${f.key}`}><ChevronUp size={13} /></button>
              <button onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-white" data-testid={`field-down-${f.key}`}><ChevronDown size={13} /></button>
              <button disabled={f.key === "CERT_NO"} title={f.key === "CERT_NO" ? "Certificate no. is required" : "Delete"} onClick={() => setList(list.filter((_, j) => j !== i))} className="p-1 text-slate-400 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`field-delete-${f.key}`}><Trash2 size={13} /></button>
            </li>
          ))}
        </ul>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); add(); }}>
          <input className="cf-input h-9 text-sm" placeholder="New field, e.g. Roll number" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="new-field-label-input" />
          <select className="cf-input h-9 text-xs shrink-0" style={{ width: 96 }} value={type} onChange={(e) => setType(e.target.value)} data-testid="new-field-type-select">
            <option value="text">Text</option><option value="image">Photo</option>
          </select>
          <button className="cf-btn-ghost h-9 text-xs shrink-0" data-testid="new-field-add-button"><Plus size={13} /> Add</button>
        </form>
        <DialogFooter>
          <button className="cf-btn-ghost h-9 text-xs" onClick={() => onOpenChange(false)} data-testid="fields-cancel-button">Cancel</button>
          <button className="cf-btn-primary h-9 text-xs px-4" onClick={save} disabled={busy} data-testid="fields-save-button">
            {busy && <Loader2 size={14} className="animate-spin" />} Save fields
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
